import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ApproveRefundModal from './ApproveRefundModal';
import RejectRefundModal from './RejectRefundModal';

const RefundsTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approveModal, setApproveModal] = useState({ isOpen: false, refund: null });
  const [rejectModal, setRejectModal] = useState({ isOpen: false, refund: null });

  const [refunds, setRefunds] = useState([
    {
      id: 1,
      clientName: 'Acme Corp',
      refundAmount: 14999,
      reason: 'Service not as expected',
      approvedBy: null,
      date: '2026-02-08',
      status: 'pending'
    },
    {
      id: 2,
      clientName: 'Tech Solutions Ltd',
      refundAmount: 5999,
      reason: 'Duplicate payment',
      approvedBy: 'Admin User',
      date: '2026-02-07',
      status: 'approved'
    },
    {
      id: 3,
      clientName: 'StartupXYZ',
      refundAmount: 2999,
      reason: 'Billing error',
      approvedBy: 'Admin User',
      date: '2026-02-06',
      status: 'rejected'
    }
  ]);

  const handleApprove = (refund) => {
    setApproveModal({ isOpen: true, refund });
  };

  const handleReject = (refund) => {
    setRejectModal({ isOpen: true, refund });
  };

  const confirmApprove = () => {
    setRefunds(refunds?.map(r => 
      r?.id === approveModal?.refund?.id 
        ? { ...r, status: 'approved', approvedBy: 'Admin User' } 
        : r
    ));
    setApproveModal({ isOpen: false, refund: null });
  };

  const confirmReject = () => {
    setRefunds(refunds?.map(r => 
      r?.id === rejectModal?.refund?.id 
        ? { ...r, status: 'rejected', approvedBy: 'Admin User' } 
        : r
    ));
    setRejectModal({ isOpen: false, refund: null });
  };

  const filteredRefunds = refunds?.filter(refund => {
    const matchesSearch = refund?.clientName?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || refund?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending', icon: 'Clock' },
      approved: { bg: 'bg-success/10', text: 'text-success', label: 'Approved', icon: 'CheckCircle' },
      rejected: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Rejected', icon: 'XCircle' }
    };
    const statusConfig = config?.[status] || config?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.text}`}>
        <Icon name={statusConfig?.icon} size={12} />
        {statusConfig?.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Search client name..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Client Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Refund Amount</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Reason</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Approved By</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRefunds?.map((refund) => (
              <tr key={refund?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-4 font-medium text-foreground">{refund?.clientName}</td>
                <td className="px-4 py-4 text-foreground font-semibold">₹{refund?.refundAmount?.toLocaleString()}</td>
                <td className="px-4 py-4 text-foreground">{refund?.reason}</td>
                <td className="px-4 py-4 text-foreground">
                  {refund?.approvedBy || <span className="text-muted-foreground">-</span>}
                </td>
                <td className="px-4 py-4 text-foreground">{new Date(refund?.date)?.toLocaleDateString()}</td>
                <td className="px-4 py-4">{getStatusBadge(refund?.status)}</td>
                <td className="px-4 py-4">
                  {refund?.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApprove(refund)}
                        className="p-2 hover:bg-success/10 rounded-md transition-smooth"
                        title="Approve"
                      >
                        <Icon name="CheckCircle" size={16} className="text-success" />
                      </button>
                      <button
                        onClick={() => handleReject(refund)}
                        className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
                        title="Reject"
                      >
                        <Icon name="XCircle" size={16} className="text-destructive" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredRefunds?.map((refund) => (
          <div key={refund?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-foreground">{refund?.clientName}</h3>
                <p className="text-sm text-muted-foreground mt-1">{new Date(refund?.date)?.toLocaleDateString()}</p>
              </div>
              {getStatusBadge(refund?.status)}
            </div>
            <div className="space-y-2 mb-4">
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="text-sm font-semibold text-foreground">₹{refund?.refundAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Reason</p>
                <p className="text-sm text-foreground">{refund?.reason}</p>
              </div>
              {refund?.approvedBy && (
                <div>
                  <p className="text-xs text-muted-foreground">Approved By</p>
                  <p className="text-sm text-foreground">{refund?.approvedBy}</p>
                </div>
              )}
            </div>
            {refund?.status === 'pending' && (
              <div className="flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleApprove(refund)}
                  iconName="CheckCircle"
                  iconPosition="left"
                  fullWidth
                >
                  Approve
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleReject(refund)}
                  iconName="XCircle"
                  iconPosition="left"
                  fullWidth
                >
                  Reject
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modals */}
      <ApproveRefundModal
        isOpen={approveModal?.isOpen}
        refund={approveModal?.refund}
        onClose={() => setApproveModal({ isOpen: false, refund: null })}
        onConfirm={confirmApprove}
      />
      <RejectRefundModal
        isOpen={rejectModal?.isOpen}
        refund={rejectModal?.refund}
        onClose={() => setRejectModal({ isOpen: false, refund: null })}
        onConfirm={confirmReject}
      />
    </div>
  );
};

export default RefundsTab;